import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, TextButton } from '@safetyhub/ui';
import { formatToLocal } from '@safetyhub/shared';
import { apiErrorMessage } from '../../lib/api';
import {
  createOrgAnnouncement,
  deleteOrgAnnouncement,
  fetchOrgAnnouncements,
  updateOrgAnnouncement,
  type OrgAnnouncement,
  type OrgAnnouncementInput,
} from '../../lib/announcements';
import { usePermissions } from '../../hooks/usePermissions';
import { useEntityChanged } from '../../hooks/useEntityChanged';
import { useAuthStore } from '../../stores/auth';
import { useToastStore } from '../../stores/toast';
import { ConfirmActionModal } from '../../components/ConfirmActionModal';
import AnnouncementFormModal from './announcements/AnnouncementFormModal';
import {
  ANNOUNCEMENT_STATE_LABELS,
  announcementVisibility,
  type AnnouncementState,
} from './announcements/announcementStatus';

/** 상태 배지 색 — 상태 넷 전부에 대해 exhaustive하다 */
const STATE_TONES: Record<AnnouncementState, 'emerald' | 'slate' | 'blue'> = {
  DRAFT: 'slate',
  SCHEDULED: 'blue',
  LIVE: 'emerald',
  ENDED: 'slate',
};

/**
 * 조직 공지 관리 (`/settings/announcements`) — 줄기 `popup-and-announcement-surface` P4.
 *
 * 진입 게이트는 **권한 key `announcement.manage`** 다 — 서버 `announcements.controller.ts`가
 * **GET까지** 그 키로 가드하므로(초안·숨김·만료를 담은 **저작 화면의 목록**이다) 소유자 성질로
 * 좁히면 「서버는 허용하는데 화면에 없는」 자리가 된다. 소유자는 서버가 전체 키를 주므로
 * (`resolvePermissions`) 키로 묻는 것이 소유자를 **이미 포함**한다. 내비 항목도 **같은 키**를
 * 읽는다(`layoutNavVisibility.ts`의 거울 규칙 — 넓으면 막다른 길, 좁으면 dead grant).
 * 형판은 `pages/settings/Roles.tsx`다.
 *
 * ⚠️ **전사 공지는 이 화면으로 만들 수도 고칠 수도 없다** — 서버가 조회·수정·삭제에
 * `organizationId`를 함께 넣으므로 다른 조직·전사 행은 애초에 보이지 않는다. 전사 경로는
 * 슈퍼관리자(admin 앱)의 몫이다.
 *
 * ⚠️ 구성원이 보는 게시판은 **다른 화면**(`/announcements`)이고 권한을 요구하지 않는다.
 */
export default function AnnouncementsSettings() {
  const navigate = useNavigate();
  const { currentMembership } = useAuthStore();
  const { can } = usePermissions();
  const { showToast } = useToastStore();
  const companyId = currentMembership?.companyId;
  const canManageAnnouncements = can('announcement.manage');

  const [rows, setRows] = useState<OrgAnnouncement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<OrgAnnouncement | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<OrgAnnouncement | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!canManageAnnouncements) navigate('/');
  }, [canManageAnnouncements, navigate]);

  // silent: 실시간 재조회(`ENTITY_CHANGED`) 경로 — 목록을 「불러오는 중」 스피너로 갈아 끼우지
  // 않는다(선례 `Groups.tsx`의 `loadGroups({ silent })`). 그 토글은 아래 렌더의 `loading ? …`에서
  // 목록을 통째로 지우므로, 남이 공지를 하나 고칠 때마다 보던 목록이 사라지게 된다.
  const load = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!companyId) return;
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      setRows(await fetchOrgAnnouncements(companyId));
      setError(null);
    } catch (err) {
      // 실시간 재조회의 실패는 보던 목록을 에러 화면으로 갈아 끼우지 않는다 — 직전 목록(또는 직전
      // 에러)을 그대로 두고 다음 변경 알림·새로고침이 다시 시도한다(리뷰 1103).
      if (silent) return;
      setError(apiErrorMessage(err) ?? '공지를 불러오지 못했습니다.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (!canManageAnnouncements) return;
    void load();
  }, [canManageAnnouncements, load]);

  /**
   * 남이(또는 내 다른 세션이) 공지를 쓰면 목록을 다시 받는다 — 서버 `ENTITY_CHANGED(announcement)`
   * (줄기 P9, 2026-10-27). 이 화면은 react-query를 쓰지 않으므로 무효화가 닿지 않는다.
   *
   * ⚠️ **`paused`가 필수다** — 작성·수정 모달이 열려 있는 동안 재조회가 돌면 `rows`가 바뀌는데,
   * `editTarget`은 **그 배열의 옛 행 객체**를 들고 있고 모달이 그것을 초기값으로 쓴다. 재조회
   * 자체가 입력을 지우지는 않지만(모달이 언마운트되지 않는다) 「목록과 편집 중인 값이 어긋난
   * 채 저장」이 가능해진다. 선례 `Groups.tsx`가 `{ paused: showCreateModal }`로 같은 자세다.
   *
   * ⚠️ 삭제 확인 모달(`deleteTarget`)도 **같은 축에 넣는다** — 입력은 없지만 그 모달이 가리키는
   * 행이 재조회로 사라지면 「없는 공지를 지우겠다」를 확인받는 상태가 되고, 확인하면 404
   * 토스트가 뜬다. 미뤄 두면 닫는 순간 1회 재조회되어 목록이 맞는다.
   *
   * ⚠️ 전사 공지는 이 이벤트로 오지 않는다 — 그런데 이 화면은 **애초에 전사 공지를 보지 않으므로**
   * (서버가 `organizationId`를 where에 함께 넣는다) 그 공백이 이 화면에는 없다.
   */
  useEntityChanged(['announcement'], () => void load({ silent: true }), {
    paused: formOpen || deleteTarget !== null,
  });

  const handleSubmit = async (input: OrgAnnouncementInput) => {
    if (!companyId) return;
    setSaving(true);
    try {
      if (editTarget) {
        await updateOrgAnnouncement(companyId, editTarget.id, input);
        showToast('공지를 수정했습니다.', 'success');
      } else {
        await createOrgAnnouncement(companyId, input);
        showToast('공지를 등록했습니다.', 'success');
      }
      setFormOpen(false);
      setEditTarget(null);
      await load();
    } catch (err) {
      // 모달은 **닫지 않는다** — 입력을 잃지 않게 두고 사유만 알린다(서버 거부는 대개 조합 문제다)
      showToast(apiErrorMessage(err) ?? '저장에 실패했습니다.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!companyId || !deleteTarget) return;
    setDeleting(true);
    try {
      await deleteOrgAnnouncement(companyId, deleteTarget.id);
      showToast('공지를 삭제했습니다.', 'success');
      setDeleteTarget(null);
      await load();
    } catch (err) {
      showToast(apiErrorMessage(err) ?? '삭제에 실패했습니다.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // 권한이 없으면 위 effect가 `/`로 보낸다 — 그 한 틱 사이에 목록을 그리지 않는다.
  if (!canManageAnnouncements) return null;

  if (!companyId) {
    return (
      <div className="py-12 text-center">
        <p className="text-ink-500">조직을 선택해주세요.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-ink-900">공지 관리</h1>
          <p className="mt-1 text-sm text-ink-600">
            조직 구성원에게 보이는 공지입니다. 초안·숨김·기간이 지난 것까지 모두 보입니다.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditTarget(null);
            setFormOpen(true);
          }}
        >
          공지 작성
        </Button>
      </div>

      {loading ? (
        <div className="py-12 text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-brand-600" />
          <p className="mt-4 text-ink-500">불러오는 중...</p>
        </div>
      ) : error ? (
        <div className="py-12 text-center">
          <p className="text-rose-600">{error}</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-ink-500">등록된 공지가 없습니다.</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {rows.map((row) => {
            const { state, hidden } = announcementVisibility(row);
            return (
              <li key={row.id}>
                <Card padding="lg" radius="lg">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={STATE_TONES[state]}>{ANNOUNCEMENT_STATE_LABELS[state]}</Badge>
                    {/* 숨김은 게시·기간과 **다른 축**이라 별 배지다(`announcementStatus.ts` JSDoc) */}
                    {hidden && <Badge tone="rose">숨김</Badge>}
                    {row.pinned && <Badge tone="amber">고정</Badge>}
                  </div>
                  <p className="mt-2 font-semibold text-ink-900">{row.title}</p>
                  <p className="mt-1 line-clamp-2 whitespace-pre-line text-sm text-ink-600">
                    {row.body}
                  </p>
                  <div className="mt-2 text-xs text-ink-400">
                    {row.publishedAt
                      ? `게시 ${formatToLocal(new Date(row.publishedAt))}`
                      : `작성 ${formatToLocal(new Date(row.createdAt))}`}
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    <TextButton
                      onClick={() => {
                        setEditTarget(row);
                        setFormOpen(true);
                      }}
                    >
                      수정
                    </TextButton>
                    <TextButton tone="danger" onClick={() => setDeleteTarget(row)}>
                      삭제
                    </TextButton>
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      <AnnouncementFormModal
        open={formOpen}
        target={editTarget}
        saving={saving}
        onClose={() => {
          setFormOpen(false);
          setEditTarget(null);
        }}
        onSubmit={handleSubmit}
      />

      <ConfirmActionModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => void handleDelete()}
        title="공지를 삭제할까요?"
        message="삭제하면 구성원의 읽음 기록까지 함께 지워지고 되돌릴 수 없습니다."
        highlight={deleteTarget?.title}
        confirmLabel="삭제"
        confirmVariant="danger"
        loading={deleting}
      />
    </div>
  );
}
